import { configureStore } from '@reduxjs/toolkit';
import { TypedUseSelectorHook, useDispatch, useSelector } from 'react-redux';
import { setupListeners } from '@reduxjs/toolkit/query';
import {
  persistStore,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'redux-persist';
import {
  persistedAuthReducer,
  persistedAdminUserReducer,
  persistedOrganizationReducer,
  persistedPropertyReducer,
} from './persistConfig';
import homeReducer from './slices/residents/homeSlice';
import wifiReducer from './slices/residents/wifiSlice';
import invitationsReducer from './slices/residents/invitationsSlice';
import paymentsReducer from './slices/residents/paymentsSlice';
import roommatesReducer from './slices/residents/roommatesSlice';
import adminUIReducer from './slices/admin/adminUISlice';
import featureFlagsReducer from './slices/admin/featureFlagsSlice';
import { residentListApi } from './api/admin/residentListApi';
import { userManagementApi } from './api/admin/userManagementApi';
import { fundingApi } from './api/admin/fundingApi';
import { bulletinsApi } from './api/admin/bulletinsApi';
import { todoListsApi } from './api/admin/todoListsApi';
import { eventsApi } from './api/admin/eventsApi';
import { resourcesApi } from './api/admin/resourcesApi';
import { messagesApi } from './api/admin/messagesApi';
import { organizationsApi } from './api/admin/organizationsApi';
import { configurationsApi } from './api/admin/configurationsApi';
import { propertiesApi } from './api/admin/propertiesApi';
import { featureFlagsApi } from './api/admin/featureFlagsApi';
import { authApi } from '@/store/api/authApi';
import { leasesApi } from './api/leasesApi';
import { yardiPaymentApi } from './api/yardiPaymentApi';
import { webPaymentsCompatApi } from './api/webPaymentsCompat';
import { maintenanceApi } from './api/maintenanceApi';
import { residentTodosApi } from './api/residentTodosApi';
import { webNotificationsApi } from './api/webNotificationsApi';
// Marketplace APIs - split into domain-specific modules
import { listingsApi } from './api/marketplace/listingsApi';
import { profileApi } from './api/marketplace/profileApi';
import { socialApi } from './api/marketplace/socialApi';
import { messagesApi as marketplaceMessagesApi } from './api/marketplace/messagesApi';
import { reportsApi } from './api/marketplace/reportsApi';
import { notificationsApi as marketplaceNotificationsApi } from './api/marketplace/notificationsApi';
import { feedbackApi } from './api/marketplace/feedbackApi';
// Cork Board API
import { corkBoardApi } from './api/corkBoard/corkBoardApi';
import corkBoardUIReducer from './slices/residents/corkBoardUISlice';
// Sentiment API
import { sentimentApi } from './api/sentiment/sentimentApi';
import tableStateReducer from './slices/tableStateSlice';

export const store = configureStore({
  reducer: {
    auth: persistedAuthReducer,
    home: homeReducer,
    roommates: roommatesReducer,
    wifi: wifiReducer,
    invitations: invitationsReducer,
    payments: paymentsReducer,
    adminUser: persistedAdminUserReducer,
    adminUI: adminUIReducer,
    organization: persistedOrganizationReducer,
    property: persistedPropertyReducer,
    featureFlags: featureFlagsReducer,
    tableState: tableStateReducer,
    // Add RTK Query API reducers
    [residentListApi.reducerPath]: residentListApi.reducer,
    [userManagementApi.reducerPath]: userManagementApi.reducer,
    [fundingApi.reducerPath]: fundingApi.reducer,
    [bulletinsApi.reducerPath]: bulletinsApi.reducer,
    [todoListsApi.reducerPath]: todoListsApi.reducer,
    [eventsApi.reducerPath]: eventsApi.reducer,
    [resourcesApi.reducerPath]: resourcesApi.reducer,
    [messagesApi.reducerPath]: messagesApi.reducer,
    [organizationsApi.reducerPath]: organizationsApi.reducer,
    [configurationsApi.reducerPath]: configurationsApi.reducer,
    [propertiesApi.reducerPath]: propertiesApi.reducer,
    [featureFlagsApi.reducerPath]: featureFlagsApi.reducer,
    // Add the main API reducer for auth and other shared endpoints
    [authApi.reducerPath]: authApi.reducer,
    [leasesApi.reducerPath]: leasesApi.reducer,
    [yardiPaymentApi.reducerPath]: yardiPaymentApi.reducer,
    [webPaymentsCompatApi.reducerPath]: webPaymentsCompatApi.reducer,
    [maintenanceApi.reducerPath]: maintenanceApi.reducer,
    [residentTodosApi.reducerPath]: residentTodosApi.reducer,
    [webNotificationsApi.reducerPath]: webNotificationsApi.reducer,
    // Marketplace APIs - domain-specific modules
    [listingsApi.reducerPath]: listingsApi.reducer,
    [profileApi.reducerPath]: profileApi.reducer,
    [socialApi.reducerPath]: socialApi.reducer,
    [marketplaceMessagesApi.reducerPath]: marketplaceMessagesApi.reducer,
    [reportsApi.reducerPath]: reportsApi.reducer,
    [marketplaceNotificationsApi.reducerPath]:
      marketplaceNotificationsApi.reducer,
    [feedbackApi.reducerPath]: feedbackApi.reducer,
    // Cork Board API
    [corkBoardApi.reducerPath]: corkBoardApi.reducer,
    corkBoardUI: corkBoardUIReducer,
    // Sentiment API
    [sentimentApi.reducerPath]: sentimentApi.reducer,
  },
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      // Disable performance-heavy middleware in development for large state
      // These are automatically disabled in production builds
      immutableCheck: false,
      serializableCheck: {
        // Reduce warnAfter threshold to catch issues faster without impacting performance
        warnAfter: 128,
        ignoredActions: [
          FLUSH,
          REHYDRATE,
          PAUSE,
          PERSIST,
          PURGE,
          REGISTER,
          // Ignore RTK Query actions that might contain non-serializable data
          'residentListApi/executeQuery/pending',
          'residentListApi/executeQuery/fulfilled',
          'residentListApi/executeQuery/rejected',
          'userManagementApi/executeQuery/pending',
          'userManagementApi/executeQuery/fulfilled',
          'userManagementApi/executeQuery/rejected',
          'fundingApi/executeQuery/pending',
          'fundingApi/executeQuery/fulfilled',
          'fundingApi/executeQuery/rejected',
          'bulletinsApi/executeQuery/pending',
          'bulletinsApi/executeQuery/fulfilled',
          'bulletinsApi/executeQuery/rejected',
          'todoListsApi/executeQuery/pending',
          'todoListsApi/executeQuery/fulfilled',
          'todoListsApi/executeQuery/rejected',
          'eventsApi/executeQuery/pending',
          'eventsApi/executeQuery/fulfilled',
          'eventsApi/executeQuery/rejected',
          'resourcesApi/executeQuery/pending',
          'resourcesApi/executeQuery/fulfilled',
          'resourcesApi/executeQuery/rejected',
          'messagesApi/executeQuery/pending',
          'messagesApi/executeQuery/fulfilled',
          'messagesApi/executeQuery/rejected',
          'yardiPaymentApi/executeQuery/pending',
          'yardiPaymentApi/executeQuery/fulfilled',
          'yardiPaymentApi/executeQuery/rejected',
          'api/executeQuery/pending',
          'api/executeQuery/fulfilled',
          'api/executeQuery/rejected',
          'webPaymentsCompatApi/executeQuery/pending',
          'webPaymentsCompatApi/executeQuery/fulfilled',
          'webPaymentsCompatApi/executeQuery/rejected',
          'maintenanceApi/executeQuery/pending',
          'maintenanceApi/executeQuery/fulfilled',
          'maintenanceApi/executeQuery/rejected',
          'residentTodosApi/executeQuery/pending',
          'residentTodosApi/executeQuery/fulfilled',
          'residentTodosApi/executeQuery/rejected',
          'webNotificationsApi/executeQuery/pending',
          'webNotificationsApi/executeQuery/fulfilled',
          'webNotificationsApi/executeQuery/rejected',
          'organizationsApi/executeQuery/pending',
          'organizationsApi/executeQuery/fulfilled',
          'organizationsApi/executeQuery/rejected',
          'configurationsApi/executeQuery/pending',
          'configurationsApi/executeQuery/fulfilled',
          'configurationsApi/executeQuery/rejected',
          'propertiesApi/executeQuery/pending',
          'propertiesApi/executeQuery/fulfilled',
          'propertiesApi/executeQuery/rejected',
          'featureFlagsApi/executeQuery/pending',
          'featureFlagsApi/executeQuery/fulfilled',
          'featureFlagsApi/executeQuery/rejected',
          // Marketplace APIs
          'marketplaceListingsApi/executeQuery/pending',
          'marketplaceListingsApi/executeQuery/fulfilled',
          'marketplaceListingsApi/executeQuery/rejected',
          'marketplaceProfileApi/executeQuery/pending',
          'marketplaceProfileApi/executeQuery/fulfilled',
          'marketplaceProfileApi/executeQuery/rejected',
          'marketplaceSocialApi/executeQuery/pending',
          'marketplaceSocialApi/executeQuery/fulfilled',
          'marketplaceSocialApi/executeQuery/rejected',
          'marketplaceMessagesApi/executeQuery/pending',
          'marketplaceMessagesApi/executeQuery/fulfilled',
          'marketplaceMessagesApi/executeQuery/rejected',
          'marketplaceReportsApi/executeQuery/pending',
          'marketplaceReportsApi/executeQuery/fulfilled',
          'marketplaceReportsApi/executeQuery/rejected',
          'marketplaceNotificationsApi/executeQuery/pending',
          'marketplaceNotificationsApi/executeQuery/fulfilled',
          'marketplaceNotificationsApi/executeQuery/rejected',
          'marketplaceFeedbackApi/executeQuery/pending',
          'marketplaceFeedbackApi/executeQuery/fulfilled',
          'marketplaceFeedbackApi/executeQuery/rejected',
          // Cork Board API
          'corkBoardApi/executeQuery/pending',
          'corkBoardApi/executeQuery/fulfilled',
          'corkBoardApi/executeQuery/rejected',
          // Sentiment API
          'sentimentApi/executeQuery/pending',
          'sentimentApi/executeQuery/fulfilled',
          'sentimentApi/executeQuery/rejected',
        ],
        ignoredActionsPaths: ['meta.arg', 'payload.timestamp'],
        ignoredPaths: [
          // Ignore RTK Query cache that might contain non-serializable data
          'residentListApi',
          'userManagementApi',
          'fundingApi',
          'bulletinsApi',
          'todoListsApi',
          'eventsApi',
          'resourcesApi',
          'messagesApi',
          'yardiPaymentApi',
          'api', // Add the new main API
          'webPaymentsCompatApi',
          'maintenanceApi',
          'webNotificationsApi',
          'organizationsApi',
          'configurationsApi',
          'propertiesApi',
          'featureFlagsApi',
          // Marketplace APIs
          'marketplaceListingsApi',
          'marketplaceProfileApi',
          'marketplaceSocialApi',
          'marketplaceMessagesApi',
          'marketplaceReportsApi',
          'marketplaceNotificationsApi',
          'marketplaceFeedbackApi',
          // Cork Board API
          'corkBoardApi',
          // Sentiment API
          'sentimentApi',
        ],
      },
    }).concat(
      residentListApi.middleware,
      userManagementApi.middleware,
      fundingApi.middleware,
      bulletinsApi.middleware,
      todoListsApi.middleware,
      eventsApi.middleware,
      resourcesApi.middleware,
      messagesApi.middleware,
      organizationsApi.middleware,
      configurationsApi.middleware,
      propertiesApi.middleware,
      featureFlagsApi.middleware,
      authApi.middleware,
      leasesApi.middleware,
      yardiPaymentApi.middleware,
      webPaymentsCompatApi.middleware,
      maintenanceApi.middleware,
      residentTodosApi.middleware,
      webNotificationsApi.middleware,
      // Marketplace APIs
      listingsApi.middleware,
      profileApi.middleware,
      socialApi.middleware,
      marketplaceMessagesApi.middleware,
      reportsApi.middleware,
      marketplaceNotificationsApi.middleware,
      feedbackApi.middleware,
      // Cork Board API
      corkBoardApi.middleware,
      // Sentiment API
      sentimentApi.middleware,
    ),
});

// Enable listener behavior for the store (guard for non-DOM environments)
try {
  const g: any =
    typeof globalThis !== 'undefined' ? (globalThis as any) : undefined;
  if (g && typeof g.addEventListener === 'function') {
    setupListeners(store.dispatch);
  }
} catch (e) {
  // no-op: environment does not support focus/online listeners
}

export const persistor = persistStore(store);

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

// Typed hooks
export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

// Note: RTK Query hooks are exported from './api' to avoid duplication
// Import API hooks from '@/store/api' instead of '@/store'
